/**
 * pages/dashboard/legal-editor.tsx
 * ─────────────────────────────────
 * Page wrapper for /dashboard/collab/:documentId
 *
 * Responsibilities:
 *  1. Read :documentId from URL params
 *  2. Read optional document content from navigation state (passed by drafting page)
 *  3. Auto-save version 1 if this is the first open (state.isNew === true)
 *  4. Load latest version content from backend if no state content is available
 */
import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import LegalEditor from "@/components/editor/LegalEditor";
import { getAccessToken } from "@/lib/auth-utils";
import { marked } from "marked";

marked.setOptions({ breaks: true, gfm: true });

/** Convert markdown to HTML; pass-through if already HTML */
function toHtml(content: string): string {
    if (!content) return "";
    if (content.trim().startsWith("<")) return content;
    return marked.parse(content) as string;
}

const API = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

interface LocationState {
    content?: string;
    documentType?: string;
    isNew?: boolean;
    templatePath?: string;
}

const LegalEditorPage: React.FC = () => {
    const { documentId } = useParams<{ documentId: string }>();
    const location = useLocation();
    const { user } = useAuth();

    const state = (location.state || {}) as LocationState;
    const hasContent = Boolean(state.content && state.content.trim());
    const [initialContent, setInitialContent] = useState<string>(
        hasContent ? toHtml(state.content!) : ""
    );
    const [docTitle, setDocTitle] = useState<string>(state.documentType || "Documento jurídico");
    const [loading, setLoading] = useState(!hasContent);

    // userId is the actual UUID from Supabase auth — used for DB writes
    const userId = user?.id || "anonymous";
    const userName = user?.email?.split("@")[0] || "Abogado";

    // ── On mount: auto-save v1 if new, or load content (from DB or Storage fallback) ──
    useEffect(() => {
        if (!documentId) return;

        if (state.isNew && hasContent) {
            getAccessToken().then(token => {
                fetch(`${API}/api/v1/documents/${documentId}/versions`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token || ""}`
                    },
                    body: JSON.stringify({
                        document_id: documentId,
                        content_text: state.content,
                        edited_by: userId,  // UUID, not username
                        document_type: state.documentType || "Documento jurídico",
                        comment: `Versión inicial — ${state.documentType || "Documento"}`,
                    }),
                }).catch(err => console.warn("Could not auto-save v1:", err));
            });

            setLoading(false);
        } else {
            // Fetch content from the latest version (auth required — scoped to owner)
            getAccessToken().then(token =>
                fetch(`${API}/api/v1/documents/${documentId}/versions`, {
                    headers: { Authorization: `Bearer ${token || ""}` },
                }))
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                    if (data?.versions && data.versions.length > 0) {
                        const latest = data.versions[0]; // ordered desc by version_number
                        setInitialContent(toHtml(latest.content_text));
                        if (latest.document_type || latest.comment) {
                            setDocTitle(latest.document_type || latest.comment);
                        }
                    }
                })
                .catch(err => console.error("Error loading versions:", err))
                .finally(() => setLoading(false));
        }
    }, [documentId]); // eslint-disable-line

    if (!documentId) {
        return (
            <div style={{ padding: "2rem", color: "#94a3b8", textAlign: "center" }}>
                Error: no se encontró el ID del documento.
            </div>
        );
    }

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#f0f0f0", color: "#5f6368" }}>
                Cargando documento…
            </div>
        );
    }

    return (
        <LegalEditor
            documentId={documentId}
            documentTitle={docTitle}
            initialContent={initialContent}
            userId={userId}
            userName={userName}
            templatePath={state.templatePath}
            onSave={async (html) => {
                const token = await getAccessToken();
                const r = await fetch(`${API}/api/v1/documents/${documentId}/versions`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token || ""}`
                    },
                    body: JSON.stringify({
                        document_id: documentId,
                        document_type: docTitle || "Documento jurídico",
                        content_text: html,
                        edited_by: userId,  // UUID, not username
                        comment: docTitle || `Guardado manual — ${new Date().toLocaleString("es-CO")}`,
                    }),
                });
                if (!r.ok) {
                    const errText = await r.text().catch(() => r.statusText);
                    throw new Error(`Error guardando: ${errText}`);
                }
            }}
        />
    );
};

export default LegalEditorPage;
